import React, { useState, useEffect } from 'react';
import ProfileSettings from '../../components/ProfileSettings';
import { authService } from '../../services/auth';
import './Profile.css';
import { getTenantById, createUser, updateUser, toggleUserStatus, getSubTenants } from '../../services/api';
import { useNavigate, Link } from 'react-router-dom';

const Profile = () => {
  const navigate = useNavigate();
  const user = authService.getUser();
  const [tenant, setTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [subTenants, setSubTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubTenants, setLoadingSubTenants] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    isTenantAdmin: false
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    const fetchTenantData = async () => {
      if (user && user.tenantId) {
        try {
          const response = await getTenantById(user.tenantId);
          if (response.success && response.tenant) {
            setTenant(response.tenant);
            // Se houver usuários vinculados ao tenant
            if (response.tenant.users && Array.isArray(response.tenant.users)) {
              setTenantUsers(response.tenant.users);
            }
            
            // Buscar sub-tenants se o usuário for admin do tenant
            if (user.isTenantAdmin || user.role === 'admin') {
              fetchSubTenants(user.tenantId);
            }
          }
        } catch (error) {
          console.error('Erro ao buscar dados do tenant:', error);
        }
      }
      setLoading(false);
    };

    fetchTenantData();
  }, [user]);

  const fetchSubTenants = async (parentId) => {
    if (!parentId) return;
    
    setLoadingSubTenants(true);
    try {
      const response = await getSubTenants(parentId);
      if (response.success) {
        setSubTenants(response.subTenants || []);
      } else {
        console.error('Erro ao buscar sub-tenants:', response.error);
      }
    } catch (error) {
      console.error('Erro ao buscar sub-tenants:', error);
    } finally {
      setLoadingSubTenants(false);
    }
  };

  const isTenantAdmin = user?.isTenantAdmin || user?.role === 'admin';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      username: userToEdit.username,
      email: userToEdit.email,
      password: '',
      confirmPassword: '',
      isTenantAdmin: userToEdit.isTenantAdmin || false
    });
    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      // console.log(`Tentando alterar status do usuário ${userId} de ${currentStatus ? 'ativo' : 'inativo'} para ${!currentStatus ? 'ativo' : 'inativo'}`);
      
      const response = await toggleUserStatus(userId, !currentStatus);
      
      // console.log('Resposta da API:', response);
      
      if (response.success) {
        // Atualizar lista de usuários
        const updatedResponse = await getTenantById(user.tenantId);
        if (updatedResponse.success && updatedResponse.tenant) {
          if (updatedResponse.tenant.users && Array.isArray(updatedResponse.tenant.users)) {
            setTenantUsers(updatedResponse.tenant.users);
          }
        }
      } else {
        console.error('Erro ao alterar status do usuário:', response.error);
      }
    } catch (error) {
      console.error('Erro ao processar requisição:', error);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      isTenantAdmin: false
    });
    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    // Validações básicas
    if (!formData.name || !formData.username || !formData.email) {
      setFormError('Nome, usuário e email são obrigatórios');
      return;
    }

    // Senha é obrigatória apenas para novos usuários
    if (!editingUser && (!formData.password || !formData.confirmPassword)) {
      setFormError('Senha é obrigatória para novos usuários');
      return;
    }

    // Validar senhas quando informadas
    if (formData.password) {
      if (formData.password !== formData.confirmPassword) {
        setFormError('As senhas não conferem');
        return;
      }

      if (formData.password.length < 6) {
        setFormError('A senha deve ter pelo menos 6 caracteres');
        return;
      }
    }

    try {
      // Preparar dados do usuário
      const userData = {
        name: formData.name,
        username: formData.username,
        email: formData.email,
        role: 'client',
        isTenantAdmin: formData.isTenantAdmin,
        tenantId: user.tenantId
      };

      // Adicionar senha apenas se informada
      if (formData.password) {
        userData.password = formData.password;
      }

      let response;
      
      if (editingUser) {
        // Atualizar usuário existente
        response = await updateUser(editingUser.id, userData);
      } else {
        // Criar novo usuário
        response = await createUser(userData);
      }
      
      if (response.success) {
        setFormSuccess(editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
        
        // Atualizar lista de usuários
        const updatedResponse = await getTenantById(user.tenantId);
        if (updatedResponse.success && updatedResponse.tenant) {
          if (updatedResponse.tenant.users && Array.isArray(updatedResponse.tenant.users)) {
            setTenantUsers(updatedResponse.tenant.users);
          }
        }
        
        // Fechar modal após 2 segundos
        setTimeout(() => {
          setShowModal(false);
        }, 2000);
      } else {
        setFormError(response.error || 'Erro ao processar solicitação');
      }
    } catch (error) {
      console.error('Erro ao processar solicitação:', error);
      setFormError('Erro ao processar requisição');
    }
  };

  const handleAddSubTenant = () => {
    // Navegar para a página de criação de sub-tenant dentro do dashboard
    navigate('/dashboard/add-subtenant');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  const formatCredits = (credits) => {
    if (credits === undefined || credits === null) return '-';
    return parseFloat(credits).toFixed(2);
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>Meu Perfil</h1>
        <p>Gerencie suas informações de perfil e altere sua senha</p>
      </div>

      <div className="profile-container">
        <div className="profile-section">
          <h2>Informações Pessoais</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nome</label>
              <p>{user?.name || '-'}</p>
            </div>
            <div className="info-item">
              <label>Usuário</label>
              <p>{user?.username || '-'}</p>
            </div>
            <div className="info-item">
              <label>Email</label>
              <p>{user?.email || '-'}</p>
            </div>
            <div className="info-item">
              <label>Função</label>
              <p>{user?.role === 'admin' ? 'Administrador' : user?.isTenantAdmin ? 'Administrador de Tenant' : 'Usuário'}</p>
            </div>
          </div>
        </div>

        {tenant && (
          <div className="profile-section">
            <h2>Informações da Empresa</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Nome da Empresa</label>
                <p>{tenant.name || '-'}</p>
              </div>
              <div className="info-item">
                <label>Email</label>
                <p>{tenant.email || '-'}</p>
              </div>
              <div className="info-item">
                <label>Telefone</label>
                <p>{tenant.phone || '-'}</p>
              </div>
              <div className="info-item">
                <label>Documento</label>
                <p>{tenant.document || '-'}</p>
              </div>
              <div className="info-item">
                <label>Endereço</label>
                <p>{tenant.address || '-'}</p>
              </div>
              <div className="info-item">
                <label>Saldo</label>
                <p>{tenant.credits !== undefined ? `${tenant.credits} Créditos` : '-'}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p>{tenant.active ? 'Ativo' : 'Inativo'}</p>
              </div>
            </div>
          </div>
        )}

        {isTenantAdmin && (
          <div className="profile-section">
            <div className="section-header">
              <h2>Gerenciamento de Clientes</h2>
              <button className="add-user-btn" onClick={handleAddSubTenant}>
                <i className="fas fa-plus"></i> Adicionar Cliente
              </button>
            </div>
            <p className="section-description">
              Como administrador, você pode adicionar novos clientes vinculados à sua empresa.
              Estes clientes terão seus próprios usuários e créditos.
            </p>
            
            {loadingSubTenants ? (
              <p className="loading-message">Carregando clientes...</p>
            ) : subTenants.length > 0 ? (
              <div className="tenants-table-container">
                <table className="tenants-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Email</th>
                      <th>Créditos</th>
                      <th>Status</th>
                      <th>Data de Cadastro</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subTenants.map(subTenant => (
                      <tr key={subTenant.id}>
                        <td>{subTenant.name}</td>
                        <td>{subTenant.email}</td>
                        <td>{formatCredits(subTenant.credits)}</td>
                        <td>
                          <span className={`status-badge ${subTenant.active ? 'active' : 'inactive'}`}>
                            {subTenant.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td>{formatDate(subTenant.createdAt)}</td>
                        <td className="actions-cell">
                          <Link 
                            to={`/dashboard/tenants/${subTenant.id}`} 
                            className="btn-view"
                            title="Ver detalhes do cliente"
                          >
                            Detalhes
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data-message">Nenhum cliente vinculado encontrado.</p>
            )}
          </div>
        )}

        {isTenantAdmin && (
          <div className="profile-section">
            <div className="section-header">
              <h2>Usuários Vinculados</h2>
              <button className="add-user-btn" onClick={handleAddUser}>
                <i className="fas fa-plus"></i> Adicionar Usuário
              </button>
            </div>
            
            {tenantUsers.length > 0 ? (
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Usuário</th>
                      <th>Email</th>
                      <th>Função</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantUsers.map(tenantUser => (
                      <tr key={tenantUser.id}>
                        <td>{tenantUser.name}</td>
                        <td>{tenantUser.username}</td>
                        <td>{tenantUser.email}</td>
                        <td>{tenantUser.isTenantAdmin ? 'Admin' : 'Usuário'}</td>
                        <td>
                          <span className={`status-badge ${tenantUser.active ? 'active' : 'inactive'}`}>
                            {tenantUser.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <button 
                            className="btn-edit" 
                            onClick={() => handleEditUser(tenantUser)}
                            disabled={user.id === tenantUser.id}
                            title={user.id === tenantUser.id ? "Não é possível editar seu próprio usuário aqui" : "Editar usuário"}
                          >
                            Editar
                          </button>
                          <button 
                            className={`btn-toggle-status ${tenantUser.active ? 'deactivate' : 'activate'}`}
                            onClick={() => {
                              // console.log(`Clicou em ${tenantUser.active ? 'Desativar' : 'Ativar'} para o usuário:`, tenantUser);
                              handleToggleUserStatus(tenantUser.id, tenantUser.active);
                            }}
                            disabled={user.id === tenantUser.id}
                            title={user.id === tenantUser.id ? "Não é possível alterar status do seu próprio usuário" : tenantUser.active ? "Desativar usuário" : "Ativar usuário"}
                          >
                            {tenantUser.active ? 'Desativar' : 'Ativar'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Nenhum usuário vinculado.</p>
            )}
          </div>
        )}

        <div className="profile-section">
          <ProfileSettings />
        </div>
      </div>

      {/* Modal para adicionar ou editar usuário */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{editingUser ? 'Editar Usuário' : 'Adicionar Novo Usuário'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            {formSuccess && <div className="form-success">{formSuccess}</div>}
            {formError && <div className="form-error">{formError}</div>}
            <form onSubmit={handleSubmitUser}>
              <div className="form-group">
                <label htmlFor="name">Nome</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="username">Nome de Usuário</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">
                  Senha {editingUser && <span>(deixe em branco para manter a atual)</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  Confirmar Senha
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!editingUser || formData.password !== ''}
                />
              </div>
              <div className="form-group checkbox-group">
                <label htmlFor="isTenantAdmin">
                  <input
                    type="checkbox"
                    id="isTenantAdmin"
                    name="isTenantAdmin"
                    checked={formData.isTenantAdmin}
                    onChange={handleInputChange}
                  />
                  Administrador do Cliente
                </label>
                <small className="help-text">
                  Se marcado, este usuário poderá gerenciar outros usuários vinculados a este cliente.
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-save">
                  {editingUser ? 'Salvar Alterações' : 'Adicionar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile; 